import { ComponentInstanceValidationErrorType, Diagram, DiagramComponentInstance, DiagramConnection } from 'models';

const BATTERY_COMPONENT_TYPES: DiagramComponentInstance['componentType'][] = ['battery', 'solution'];

const StateOfChargeDeratingHelpers = {
    getMaximumPowerPointTrackingPort: (componentInstance: DiagramComponentInstance): number | null => {
        const controlMethods = componentInstance.configuration.ports.map((port) => port.controlMethod);

        const pairsWithPowerVoltage =
            controlMethods.length === 2 &&
            controlMethods.includes('maximum-power-point-tracking') &&
            controlMethods.includes('power-voltage');

        return pairsWithPowerVoltage ? controlMethods.indexOf('maximum-power-point-tracking') : null;
    },

    getConnectedBatteries: (diagram: Diagram, componentInstance: DiagramComponentInstance, port: number) => {
        const connections = Object.values(diagram.connections).filter((connection) =>
            [connection.from, connection.to].every(
                (anchor) => !diagram.componentInstances[anchor.componentInstanceId]?.disabled,
            ),
        );

        return componentInstance.configuration.ports
            .map((_, otherPort) => otherPort)
            .filter((otherPort) => otherPort !== port)
            .map((otherPort) => {
                const connection = connections.find((candidate) =>
                    [candidate.from, candidate.to].some(
                        (anchor) => anchor.componentInstanceId === componentInstance.id && anchor.port === otherPort,
                    ),
                );

                return connection ? followToEndpoint(connections, componentInstance.id, connection) : null;
            })
            .map((endpointId) => (endpointId ? diagram.componentInstances[endpointId] : undefined))
            .filter((endpoint): endpoint is DiagramComponentInstance => isBattery(endpoint));
    },

    findSource: (diagram: Diagram, source: string) =>
        diagram.componentInstances[source] ??
        Object.values(diagram.componentInstances).find((componentInstance) => componentInstance.designator === source),

    getErrorType: (diagram: Diagram, componentInstance: DiagramComponentInstance, port: number) => {
        const portConfiguration = componentInstance.configuration.ports[port];

        if (
            componentInstance.disabled ||
            portConfiguration?.controlMethod !== 'power-voltage' ||
            portConfiguration.droopCurves?.stateOfChargeDerating !== true
        ) {
            return null;
        }

        if (componentInstance.componentType !== 'converter') {
            return ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_COMPONENT;
        }

        if (StateOfChargeDeratingHelpers.getMaximumPowerPointTrackingPort(componentInstance) !== null) {
            return ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_MPPT;
        }

        const { stateOfChargeSource } = portConfiguration.droopCurves;

        if (stateOfChargeSource) {
            return isBattery(StateOfChargeDeratingHelpers.findSource(diagram, stateOfChargeSource))
                ? null
                : ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_SOURCE;
        }

        const connectedBatteries = StateOfChargeDeratingHelpers.getConnectedBatteries(diagram, componentInstance, port);

        if (connectedBatteries.length === 0) {
            return ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_STATE_OF_CHARGE_DERATING_BATTERY;
        }

        if (connectedBatteries.length > 1) {
            return ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_BATTERIES;
        }

        return null;
    },
};

const isBattery = (componentInstance: DiagramComponentInstance | undefined) =>
    !!componentInstance && BATTERY_COMPONENT_TYPES.includes(componentInstance.componentType);

const followToEndpoint = (
    connections: DiagramConnection[],
    componentInstanceId: string,
    connection: DiagramConnection,
    visitedConnectionIds: string[] = [],
): string | null => {
    const nextComponentInstanceId =
        connection.from.componentInstanceId === componentInstanceId
            ? connection.to.componentInstanceId
            : connection.from.componentInstanceId;

    const nextConnections = connections.filter(
        (candidate) =>
            candidate.from.componentInstanceId === nextComponentInstanceId ||
            candidate.to.componentInstanceId === nextComponentInstanceId,
    );

    if (nextConnections.length === 1) {
        return nextComponentInstanceId;
    }

    const onwardConnection = nextConnections.find(
        (candidate) => candidate.id !== connection.id && !visitedConnectionIds.includes(candidate.id),
    );

    if (nextConnections.length > 2 || !onwardConnection) {
        return null;
    }

    return followToEndpoint(connections, nextComponentInstanceId, onwardConnection, [
        ...visitedConnectionIds,
        connection.id,
    ]);
};

export { StateOfChargeDeratingHelpers };
